/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react';
import type { HTMLAttributes } from 'react';
import type { VariantType } from 'notistack';
import { useSnackbar } from 'notistack';
import { ERROR_400, ERROR_401, ERROR_403, ERROR_404, ERROR_409, ERROR_500 } from 'common/config/api';
import { UNCONTROLLED_ERROR } from 'common/constants/messages/validations';
import { useRefreshAccessToken } from 'pages/Auth/hooks';
import { isString } from 'utilities';

interface DefaultErrorConfig {
  defaultMessage: string;
  defaultType: VariantType;
}

const defaultConfig: Record<string, DefaultErrorConfig> = {
  [ERROR_400]: {
    defaultType: 'error',
    defaultMessage: 'Something went wrong! Review your data or contact support.',
  },
  [ERROR_401]: {
    defaultType: 'error',
    defaultMessage: 'Unauthorized request.',
  },
  [ERROR_403]: {
    defaultType: 'error',
    defaultMessage: 'You do not have permission to perform this action.',
  },
  [ERROR_404]: {
    defaultType: 'error',
    defaultMessage: 'The page or resource doesn\'t exist.',
  },
  [ERROR_409]: {
    defaultType: 'error',
    defaultMessage: 'The resource you are trying to create conflicts with an existing resource.',
  },
  [ERROR_500]: {
    defaultType: 'error',
    defaultMessage: 'We\'re experiencing some technical issues. Try later or contact support.',
  },
};

interface SnackbarProps extends HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

interface ErrorResponseConfig {
  callback?: () => void;
  message?: string;
  notification?: boolean;
  type?: VariantType;
}

interface ProcessErrorResponseParams {
  action?: string;
  config?: Record<number, ErrorResponseConfig>;
  defaultErrorMessage?: string;
  error: unknown;
}

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined;
  }

  const { status } = error as { status?: number | string };
  const numericStatus = typeof status === 'string' ? Number.parseInt(status, 10) : status;

  return typeof numericStatus === 'number' && !Number.isNaN(numericStatus) ? numericStatus : undefined;
};

const isProcessErrorResponseParams = (value: unknown): value is ProcessErrorResponseParams =>
  Boolean(value && typeof value === 'object' && 'error' in value);

export const useResponse = () => {
  const { enqueueSnackbar } = useSnackbar();
  const refreshToken = useRefreshAccessToken();

  const processErrorResponse = useCallback(
    (params: ProcessErrorResponseParams | unknown) => {
      const { error, defaultErrorMessage, config = {}, action = '' } = isProcessErrorResponseParams(params)
        ? params
        : { error: params };
      const status = getErrorStatus(error);
      const statusKey = status?.toString() ?? '';
      const { notification, message, type, callback } = status ? config[status] ?? {} : {};
      const { defaultType, defaultMessage } = defaultConfig[statusKey] ?? {};

      const variant = type ?? defaultType;
      let notificationMsg = defaultErrorMessage ?? message ?? defaultMessage ?? UNCONTROLLED_ERROR;

      if (statusKey === ERROR_401) {
        refreshToken();
      }

      if (statusKey === ERROR_403) {
        notificationMsg = action ? `You do not have permission to ${action}` : defaultMessage;
      }

      if (notification !== false) {
        enqueueSnackbar(notificationMsg, { variant: variant ?? 'error' });
      }

      if (callback) {
        callback();
      }
    },
    [enqueueSnackbar]
  );

  const successNotification = useCallback((message: string, testId = 'snackbarSuccessMessage') => {
    enqueueSnackbar(message, {
      variant: 'success',
      SnackbarProps: { 'data-testid': testId } as SnackbarProps,
    });
  }, []);

  const infoNotification = useCallback((message: string) => {
    enqueueSnackbar(message, {
      variant: 'info',
      SnackbarProps: { 'data-testid': 'snackbarInfoMessage' } as SnackbarProps,
    });
  }, []);

  const errorNotification = useCallback((message: unknown) => {
    const errorMessage = isString(message) ? message : UNCONTROLLED_ERROR;
    enqueueSnackbar(errorMessage, {
      variant: 'error',
      SnackbarProps: { 'data-testid': 'snackbarErrorMessage' } as SnackbarProps,
    });
  }, []);

  return {
    processErrorResponse,
    successNotification,
    errorNotification,
    infoNotification,
  };
};
